/**
 * Generate a random UUID (version 4). `crypto.randomUUID` only exists in
 * secure contexts, so fall back to `crypto.getRandomValues` (which works over
 * plain HTTP) when it is missing.
 */
export const generateUUID = (): string => {
	if (typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	const bytes = crypto.getRandomValues(new Uint8Array(16));
	bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
	bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
	const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
	return [
		hex.slice(0, 4).join(""),
		hex.slice(4, 6).join(""),
		hex.slice(6, 8).join(""),
		hex.slice(8, 10).join(""),
		hex.slice(10, 16).join(""),
	].join("-");
};

/**
 * Generate a random hexadecimal string from the specified number of bytes.
 */
const generateRandomString = (bytes: number): string => {
	const byteArr = crypto.getRandomValues(new Uint8Array(bytes));
	return [...byteArr]
		.map((byte) => byte.toString(16).padStart(2, "0"))
		.join("");
};

/**
 * Generate a cryptographically secure random string using the specified number
 * of bytes then encode with base64.
 *
 * Base64 encodes 6 bits per character and pads with = so the length will not
 * equal the number of randomly generated bytes.
 * @see <https://developer.mozilla.org/en-US/docs/Glossary/Base64#encoded_size_increase>
 */
export const generateRandomBase64String = (bytes: number): string => {
	return btoa(generateRandomString(bytes));
};

/**
 * Generate a 16-byte (32-character) hexadecimal string for identifying
 * workspace connection sessions.
 */
export const generateConnectionSessionId = (): string => {
	return generateRandomString(16);
};
